import type { JsonSchema, TransformResult, TransformOptions } from "./types";

/**
 * Check if branch is an object schema
 */
function isObjectBranch(branch: any): boolean {
  if (!branch || typeof branch !== "object") return false;
  if (branch.type === "object") return true;
  if (branch.properties && typeof branch.properties === "object") return true;
  return false;
}

/**
 * Derive a stable, display-friendly ID for a oneOf/anyOf branch
 *
 * Priority:
 * 1. branch.title
 * 2. Last segment of $ref (e.g., "#/definitions/Foo" → "Foo")
 * 3. Property with const value (discriminator pattern)
 * 4. "Option_N"
 */
function getBranchId(branch: any, index: number): string {
  if (
    branch?.title &&
    typeof branch.title === "string" &&
    branch.title.trim()
  ) {
    return branch.title.trim();
  }

  if (branch?.$ref && typeof branch.$ref === "string") {
    const segments = branch.$ref.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    if (last) return last;
  }

  // Look for discriminator property with const
  const props =
    branch?.properties && typeof branch.properties === "object"
      ? branch.properties
      : {};
  for (const v of Object.values(props) as any[]) {
    if (
      v?.const !== undefined &&
      v?.const !== null &&
      typeof v.const === "string" &&
      v.const.trim()
    ) {
      return v.const.trim();
    }
  }

  return `Option_${index + 1}`;
}

/**
 * Step 4: Transform object-variant anyOf to oneOf with discriminators
 *
 * Converts anyOf where all branches are objects into oneOf with injected
 * discriminator fields. This allows using the OneOfDropdownRenderer.
 *
 * Example:
 * anyOf: [{ type: "object", title: "A", props... }, { type: "object", title: "B", props... }]
 * →
 * oneOf: [
 *   { type: "object", title: "A", properties: { prop_type: { const: "A" }, ...props } },
 *   { type: "object", title: "B", properties: { prop_type: { const: "B" }, ...props } }
 * ]
 *
 * Also marks signal_parameters with __optionalSection flag.
 *
 * @param schema - Schema after normalizeScalarAnyOf
 * @param options - Transform options
 * @returns Transformed schema and metadata
 */
export function transformObjectAnyOf(
  schema: JsonSchema,
  options?: TransformOptions
): TransformResult {
  const { debug = false } = options || {};

  if (debug) {
    console.log("🔧 [transformObjectAnyOf] Starting...");
  }

  const transformedKeys: string[] = [];
  const flags: Record<string, any> = {};

  if (!schema?.properties || typeof schema.properties !== "object") {
    if (debug) {
      console.log("⏭️  [transformObjectAnyOf] Skipped: No properties");
    }

    return {
      schema,
      metadata: {
        name: "transformObjectAnyOf",
        transformedKeys: [],
        flags: {},
        skipped: true,
        reason: "No properties",
      },
    };
  }

  Object.keys(schema.properties).forEach((propKey) => {
    const prop = schema.properties[propKey];
    if (
      !prop ||
      typeof prop !== "object" ||
      !Array.isArray(prop.anyOf) ||
      prop.anyOf.length === 0
    ) {
      return;
    }

    const branches = prop.anyOf as any[];
    if (!branches.every(isObjectBranch)) {
      return;
    }

    const discriminatorKey = `${propKey}_type`;
    const oneOfBranches = branches.map((branch: any, i: number) => {
      const branchId = getBranchId(branch, i);
      const props =
        branch.properties && typeof branch.properties === "object"
          ? { ...branch.properties }
          : {};
      const required = Array.isArray(branch.required)
        ? [...branch.required]
        : [];

      if (!required.includes(discriminatorKey)) {
        required.unshift(discriminatorKey);
      }

      return {
        type: "object",
        title: branchId,
        required,
        properties: {
          [discriminatorKey]: {
            type: "string",
            const: branchId,
            title: branchId,
          },
          ...props,
        },
      };
    });

    const newProp: any = {
      ...prop,
      oneOf: oneOfBranches,
    };
    delete newProp.anyOf;

    // Special flag for signal_parameters
    if (propKey === "signal_parameters") {
      newProp.__optionalSection = true;
      flags[propKey] = "__optionalSection";
    }

    schema.properties[propKey] = newProp;
    transformedKeys.push(propKey);

    if (debug) {
      console.log(
        `  ✓ Transformed ${propKey} anyOf → oneOf with discriminator ${discriminatorKey}`
      );
    }
  });

  // Add metadata to schema root
  if (transformedKeys.length > 0) {
    (schema as any).__anyOfTransformed = transformedKeys;
  }

  if (debug) {
    console.log("✅ [transformObjectAnyOf] Complete", {
      transformedKeys,
      flags: Object.keys(flags),
    });
  }

  return {
    schema,
    metadata: {
      name: "transformObjectAnyOf",
      transformedKeys,
      flags,
      skipped: transformedKeys.length === 0,
    },
  };
}
